/**
 * Cart invoice (Τιμολόγιο) block — markup in snippets/cart-invoice.liquid.
 *
 * The checkbox opens the ΑΦΜ / Επιχείρηση fields; "Save" validates them and stores
 * them as cart attributes (Timologio, AFM, Epixeirisi). Unchecking clears everything.
 *
 * The AJAX cart (Cart.buildCart in shop.js) throws the whole cart DOM away on every
 * quantity change, so the state lives here in memory and is re-applied with
 * CartInvoice.sync(cart.attributes) after each render. All listeners are delegated
 * on document for the same reason.
 */
(function () {
	'use strict';

	var VAT_LENGTH = 9;
	var EMPTY_ATTRIBUTES = { Timologio: '', AFM: '', Epixeirisi: '' };

	var selectors = {
		root: '[data-cart-invoice]',
		checkbox: '[data-invoice-checkbox]',
		label: '[data-invoice-label]',
		fields: '[data-invoice-fields]',
		vat: '[data-invoice-vat]',
		company: '[data-invoice-company]',
		save: '[data-invoice-save]',
		error: '[data-invoice-error]'
	};

	/**
	 * checked — checkbox state
	 * open    — fields expanded
	 * saved   — details stored on the cart
	 * vat / company — current field values (draft or saved)
	 */
	var state = {
		checked: false,
		open: false,
		saved: false,
		vat: '',
		company: ''
	};

	var errors = { vat: '', company: '' };

	function getRoot() {
		return document.querySelector(selectors.root);
	}

	function digitsOnly(value) {
		return String(value || '').replace(/\D/g, '').slice(0, VAT_LENGTH);
	}

	/** Seed state from the server-rendered block (first paint). */
	function readStateFromDom() {
		var root = getRoot();
		if (!root) return;

		var checkbox = root.querySelector(selectors.checkbox);
		var vat = root.querySelector(selectors.vat);
		var company = root.querySelector(selectors.company);

		state.saved = root.getAttribute('data-invoice-saved') === 'true';
		state.checked = checkbox ? checkbox.checked : state.saved;
		state.vat = vat ? digitsOnly(vat.value) : '';
		state.company = company ? company.value.trim() : '';
	}

	/** Write state into whatever invoice block is currently in the DOM. */
	function render() {
		var root = getRoot();
		if (!root) return;

		var checkbox = root.querySelector(selectors.checkbox);
		var label = root.querySelector(selectors.label);
		var fields = root.querySelector(selectors.fields);
		var vat = root.querySelector(selectors.vat);
		var company = root.querySelector(selectors.company);

		root.setAttribute('data-invoice-saved', state.saved ? 'true' : 'false');
		root.setAttribute('data-invoice-open', state.open ? 'true' : 'false');

		if (checkbox) checkbox.checked = state.checked;
		if (fields) fields.hidden = !state.open;
		if (vat && vat.value !== state.vat) vat.value = state.vat;
		if (company && company.value !== state.company) company.value = state.company;

		if (label) {
			label.textContent = state.saved
				? root.getAttribute('data-label-saved')
				: root.getAttribute('data-label-default');
			label.setAttribute('aria-expanded', state.open ? 'true' : 'false');
		}

		root.querySelectorAll(selectors.error).forEach(function (el) {
			var message = errors[el.getAttribute('data-invoice-error')] || '';
			el.textContent = message;
			el.hidden = !message;
		});

		if (vat) toggleInvalid(vat, !!errors.vat);
		if (company) toggleInvalid(company, !!errors.company);
	}

	function toggleInvalid(input, isInvalid) {
		input.classList.toggle('cart-invoice__input--error', isInvalid);
		if (isInvalid) {
			input.setAttribute('aria-invalid', 'true');
		} else {
			input.removeAttribute('aria-invalid');
		}
	}

	function clearErrors() {
		errors.vat = '';
		errors.company = '';
	}

	/**
	 * Fill `errors` from the current values.
	 * @returns {'vat'|'company'|null} first invalid field
	 */
	function validate() {
		var root = getRoot();
		if (!root) return null;

		clearErrors();

		if (state.vat.length === 0) {
			errors.vat = root.getAttribute('data-error-vat-required');
		} else if (state.vat.length !== VAT_LENGTH) {
			errors.vat = root.getAttribute('data-error-vat-length');
		}

		if (state.company.length === 0) {
			errors.company = root.getAttribute('data-error-company');
		}

		if (errors.vat) return 'vat';
		if (errors.company) return 'company';
		return null;
	}

	function focusField(name) {
		var root = getRoot();
		var input = root && root.querySelector(name === 'vat' ? selectors.vat : selectors.company);
		if (input) input.focus();
	}

	/**
	 * @param {Record<string, string>} attributes
	 * @returns {Promise<void>}
	 */
	function persist(attributes) {
		return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/update.js' : '/cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({ attributes: attributes })
		}).then(function (response) {
			if (!response.ok) throw new Error('Failed to save invoice details (' + response.status + ')');
		}).catch(function (error) {
			// The inputs belong to the cart form, so the details still reach checkout on
			// submit — a failed background save is worth logging, not blocking.
			console.warn('[cart-invoice]', error);
		});
	}

	/** Checkbox: on → open the fields, off → wipe the invoice. */
	function onCheckboxChange(checkbox) {
		if (checkbox.checked) {
			state.checked = true;
			state.open = true;
			render();
			focusField('vat');
			return;
		}

		var wasSaved = state.saved;
		state.checked = false;
		state.open = false;
		state.saved = false;
		state.vat = '';
		state.company = '';
		clearErrors();
		render();

		if (wasSaved) persist(EMPTY_ATTRIBUTES);
	}

	/** Label: expand the saved details for editing, or collapse them. Never clears. */
	function onLabelToggle() {
		if (state.open) {
			close();
			return;
		}

		state.checked = true;
		state.open = true;
		render();
	}

	/** Collapse, keeping what was typed. Collapsing with nothing typed switches it off. */
	function close() {
		var hasContent = state.vat.length > 0 || state.company.length > 0;

		state.open = false;
		clearErrors();

		if (!hasContent) {
			var wasSaved = state.saved;
			state.checked = false;
			state.saved = false;
			if (wasSaved) persist(EMPTY_ATTRIBUTES);
		}

		render();
	}

	function onSave(button) {
		var firstInvalid = validate();
		render();

		if (firstInvalid) {
			focusField(firstInvalid);
			return;
		}

		button.disabled = true;

		persist({ Timologio: 'Yes', AFM: state.vat, Epixeirisi: state.company }).then(function () {
			state.checked = true;
			state.saved = true;
			state.open = false;
			render();
		}).then(function () {
			button.disabled = false;
		});
	}

	/** Don't let the cart form go to checkout with a ticked but incomplete invoice. */
	function onFormSubmit(event) {
		var root = getRoot();
		if (!root || !state.checked) return;
		if (!root.closest('form') || root.closest('form') !== event.target) return;

		var firstInvalid = validate();
		if (!firstInvalid) return;

		event.preventDefault();
		event.stopImmediatePropagation();

		state.open = true;
		render();
		focusField(firstInvalid);
	}

	function bind() {
		document.addEventListener('change', function (event) {
			var checkbox = event.target.closest && event.target.closest(selectors.checkbox);
			if (checkbox) onCheckboxChange(checkbox);
		});

		document.addEventListener('input', function (event) {
			var target = event.target;
			if (!target.matches) return;

			if (target.matches(selectors.vat)) {
				var digits = digitsOnly(target.value);
				if (target.value !== digits) target.value = digits;
				state.vat = digits;
				if (errors.vat) {
					errors.vat = '';
					render();
				}
				return;
			}

			if (target.matches(selectors.company)) {
				state.company = target.value.trim();
				if (errors.company) {
					errors.company = '';
					render();
				}
			}
		});

		document.addEventListener('click', function (event) {
			if (!event.target.closest) return;

			var saveButton = event.target.closest(selectors.save);
			if (saveButton) {
				event.preventDefault();
				onSave(saveButton);
				return;
			}

			if (event.target.closest(selectors.label)) {
				onLabelToggle();
			}
		});

		document.addEventListener('keydown', function (event) {
			if (!event.target.closest) return;

			// The label is a span with role="button" — give it the usual button keys.
			if (event.target.closest(selectors.label) && (event.key === 'Enter' || event.key === ' ')) {
				event.preventDefault();
				onLabelToggle();
				return;
			}

			// Enter inside a field saves instead of submitting the cart form.
			if (event.key === 'Enter' && event.target.closest(selectors.fields)) {
				var root = getRoot();
				var saveButton = root && root.querySelector(selectors.save);
				if (saveButton) {
					event.preventDefault();
					onSave(saveButton);
				}
			}
		});

		// Capture phase so this runs before the theme's own submit handlers.
		document.addEventListener('submit', onFormSubmit, true);
	}

	window.CartInvoice = {
		/**
		 * Re-apply state after the AJAX cart re-renders.
		 * @param {Record<string, string>} [attributes] cart.attributes from the cart JSON
		 */
		sync: function (attributes) {
			// Mid-edit: keep the draft the customer is typing.
			if (attributes && !state.open) {
				state.saved = attributes.Timologio === 'Yes';
				state.checked = state.saved;
				state.vat = state.saved ? digitsOnly(attributes.AFM) : '';
				state.company = state.saved ? String(attributes.Epixeirisi || '') : '';
			}
			render();
		}
	};

	function init() {
		readStateFromDom();
		render();
		bind();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
